import { useMemo } from 'react';

import {
  buildSetupPaymentSummaryFromProcessorRecord,
  type SetupActivePayment,
} from '@/components/setup/experience/lib/setupPaymentSummary';
import { usePaymentFlowWithProcessors } from '@/lib/api/paymentFlows';
import { useStore } from '@/lib/api/stores';

/** Resolves the payment currently live on the store from the DB (not the wizard session). */
export function useSetupActivePayment(storeId: string | undefined, enabled = true) {
  const storeQuery = useStore(storeId ?? '', Boolean(storeId && enabled));
  const flowId = storeQuery.data?.selectedPaymentFlowId ?? null;
  const flowQuery = usePaymentFlowWithProcessors(flowId ?? '', Boolean(flowId && enabled));

  const active = useMemo<SetupActivePayment | null>(() => {
    const flow = flowQuery.data;
    if (!flowId || !flow) return null;

    const configs = flow.processorConfigs ?? [];
    const config = configs.find((entry) => entry.processor && !entry.processor.archivedAt);
    const processor = config?.processor;
    if (!config || !processor) return null;

    const summary = buildSetupPaymentSummaryFromProcessorRecord(processor.name, processor.type) ?? {
      processorName: processor.type,
      processorLogo: '',
    };

    return {
      flowId,
      processorId: config.processorId,
      processorType: processor.type,
      isSoloFlow: configs.length === 1,
      summary,
    };
  }, [flowId, flowQuery.data]);

  return {
    active,
    isLoading: storeQuery.isLoading || (Boolean(flowId) && flowQuery.isLoading),
  };
}
